import { createSlice, Draft } from '@reduxjs/toolkit';
import {
  fetchSimpleProjectCombine,
  fetchSimpleProjectCompare,
} from 'reduxStore/simpleProject/asyncActions';
import { Resource } from 'model/Resource';
import { SimpleProjectCombine } from 'model/SimpleProjectCombine';
import { SimpleProjectCompare } from 'model/SimpleProjectCompare';
import {
  fetchSummaryPulseCombined,
  fetchSummaryPulseCompared,
  fetchSummaryPulseLifecycleCombined,
  fetchSummaryPulseLifecycleCompared,
  fetchSummaryPulseOnDemandCombined,
  fetchSummaryPulseOnDemandCompared,
} from 'reduxStore/summary/asyncActions';

import { SimpleProjectState, STORE_NAME } from './initialState';

export const simpleProjectSlice = createSlice({
  name: STORE_NAME,
  initialState: SimpleProjectState.INITIAL_DOMAIN,
  reducers: {},
  extraReducers: (builder) => {
    const combineActions = [
      fetchSimpleProjectCombine,
      fetchSummaryPulseCombined,
      fetchSummaryPulseOnDemandCombined,
      fetchSummaryPulseLifecycleCombined,
    ];

    const compareActions = [
      fetchSimpleProjectCompare,
      fetchSummaryPulseCompared,
      fetchSummaryPulseOnDemandCompared,
      fetchSummaryPulseLifecycleCompared,
    ];

    const allActions = [...combineActions, ...compareActions];

    // Handle pending states for all actions
    builder.addMatcher(
      (action) => allActions.some((asyncAction) => asyncAction.pending.match(action)),
      (state) => {
        state.combine = Resource.createPendingResource() as Draft<Resource<SimpleProjectCombine>>;
        state.compare = Resource.createPendingResource() as Draft<Resource<SimpleProjectCompare>>;
      }
    );

    // Handle fulfilled states for combine actions
    builder.addMatcher(
      (action) => combineActions.some((asyncAction) => asyncAction.fulfilled.match(action)),
      (state, action) => {
        if (action.payload?.simpleProject) {
          state.combine = Resource.createResolvedResource({
            data: action.payload.simpleProject,
          }) as Draft<Resource<SimpleProjectCombine>>;
        }
      }
    );

    // Handle fulfilled states for compare actions
    builder.addMatcher(
      (action) => compareActions.some((asyncAction) => asyncAction.fulfilled.match(action)),
      (state, action) => {
        if (action.payload?.simpleProject) {
          state.compare = Resource.createResolvedResource({
            data: action.payload.simpleProject,
          }) as Draft<Resource<SimpleProjectCompare>>;
        }
      }
    );

    // Handle rejected states for combine actions
    builder.addMatcher(
      (action) => combineActions.some((asyncAction) => asyncAction.rejected.match(action)),
      (state, action) => {
        state.combine = Resource.createRejectedResource({
          error: action.payload ?? action.error,
        }) as Draft<Resource<SimpleProjectCombine>>;
      }
    );

    // Handle rejected states for compare actions
    builder.addMatcher(
      (action) => compareActions.some((asyncAction) => asyncAction.rejected.match(action)),
      (state, action) => {
        state.compare = Resource.createRejectedResource({
          error: action.payload ?? action.error,
        }) as Draft<Resource<SimpleProjectCompare>>;
      }
    );
  },
});
